import React, { forwardRef } from "react";

/**
 * A reusable select dropdown with loading/disabled states and custom label support.
 *
 * @param {Object} props
 * @param {Array}  props.options - Array of option objects (must have an `_id` field)
 * @param {string} props.value - Currently selected value (matches option._id)
 * @param {function} props.onChange - Callback (value) => void
 * @param {string} [props.placeholder] - Placeholder text
 * @param {boolean} [props.loading] - Show loading state
 * @param {boolean} [props.disabled] - Disable the select
 * @param {string} [props.className] - Extra CSS for the wrapper
 * @param {function} [props.getOptionLabel] - Custom label extractor
 */
const CustomSelect = forwardRef(
  (
    {
      options,
      value,
      onChange,
      placeholder = "Select an option",
      loading = false,
      disabled = false,
      className = "",
      getOptionLabel = (opt) =>
        opt.name || opt.room_name || opt.room_number || opt._id,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    const handleChange = (e) => {
      onChange(e.target.value);
    };

    return (
      <div className={`relative flex-shrink-0 ${className}`}>
        <select
          ref={ref}
          value={value}
          onChange={handleChange}
          disabled={isDisabled}
          {...rest}
          className={`
            appearance-none
            bg-gray-50
            border border-gray-300
            text-gray-900 text-sm
            rounded-lg
            focus:ring-2 focus:ring-teal-500 focus:border-teal-500
            block w-full
            pl-3 pr-10 py-2.5
            disabled:bg-gray-100 disabled:cursor-not-allowed
            transition-colors
            ${isDisabled ? "text-gray-400" : ""}
          `}
        >
          <option value="">{loading ? "Loading..." : placeholder}</option>
          {options.map((opt) => (
            <option key={opt._id} value={opt._id}>
              {getOptionLabel(opt)}
            </option>
          ))}
        </select>

        {/* Chevron icon */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <svg
            className={`w-4 h-4 ${
              isDisabled ? "text-gray-300" : "text-gray-500"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    );
  },
);

CustomSelect.displayName = "CustomSelect";

export default CustomSelect;
